import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import type { ProductSizeInterface } from '../../../../../../../Interface/ProductSize';
import FlavourGrid from './FlavourGrid/FlavourGrid';
import type { BasketAddStatus } from '../useBoothBasket';
import {
  formatDiscount,
  formatNumber,
  formatPrice,
  lineSheetPrice,
} from '../pricing';
import styles from '../AssortmentPanel.module.scss?module';

const NBSP = '\u00a0';

interface LineSheetRowProps {
  item: ProductSizeInterface;
  /** True when the customer stocks this item (size). */
  isStocked: boolean;
  expanded: boolean;
  /** Toggles the row by the item's ERP id. */
  onToggle: (erpId: number) => void;
  /** Entered quantity per flavour (shop id). */
  quantities: Record<number, number>;
  onQuantityChange: (attributeId: number, quantity: number) => void;
  basketUnitsByAttributeId: Map<number, number>;
  status: BasketAddStatus;
  onAdd: (item: ProductSizeInterface) => void;
}

/** One item of the line sheet; the head opens and closes the flavour matrix. */
export default function LineSheetRow({
  item,
  isStocked,
  expanded,
  onToggle,
  quantities,
  onQuantityChange,
  basketUnitsByAttributeId,
  status,
  onAdd,
}: LineSheetRowProps) {
  const price = lineSheetPrice(item);
  const gridId = 'booth-line-' + item.erp_id;
  const details = itemDetails(item, price.currency);

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.rowHead}
        aria-expanded={expanded}
        aria-controls={expanded ? gridId : undefined}
        onClick={() => onToggle(item.erp_id)}
      >
        <span className={styles.rowImage}>
          {item.image_uri ? (
            <img
              src={item.image_uri}
              alt=""
              width={56}
              height={56}
              loading="lazy"
              decoding="async"
            />
          ) : null}
        </span>
        <span className={styles.rowMain}>
          <span className={styles.rowTitle}>
            <span className={styles.rowName}>{item.name}</span>
            {isStocked && (
              <span className={styles.stockBadge}>You stock this</span>
            )}
          </span>
          {details && <span className={styles.rowDetails}>{details}</span>}
        </span>
        <span className={styles.priceBlock}>
          {price.discount > 0 && (
            <span className={styles.discountBadge}>
              {formatDiscount(price.discount)}
            </span>
          )}
          <span className={styles.priceValues}>
            {price.current !== null ? (
              <span className={styles.priceCurrent}>
                {formatPrice(price.current, price.currency)}
              </span>
            ) : (
              <span className={styles.priceMissing}>No price</span>
            )}
            {price.regular !== null && (
              <span className={styles.priceRegular}>
                <span className={styles.visuallyHidden}>Regular price </span>
                {formatPrice(price.regular, price.currency)}
              </span>
            )}
          </span>
        </span>
        <span className={styles.chevron} aria-hidden="true">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </button>
      {expanded && (
        <div id={gridId} className={styles.rowBody}>
          <FlavourGrid
            item={item}
            price={price}
            quantities={quantities}
            onQuantityChange={onQuantityChange}
            basketUnitsByAttributeId={basketUnitsByAttributeId}
            status={status}
            onAdd={onAdd}
          />
        </div>
      )}
    </li>
  );
}

/**
 * "Bars · 12 pieces · 1 per case · deposit €0.25"; parts without data are left out. Measures
 * keep their number and unit together (no-break space), so lines only wrap at the dots.
 */
function itemDetails(item: ProductSizeInterface, currency: string): string {
  const parts: string[] = [];
  const categoryName = item.category?.name?.trim();
  if (categoryName) {
    parts.push(categoryName);
  }
  if (typeof item.unit_value === 'number' && item.unit_value > 0) {
    parts.push(
      [formatNumber(item.unit_value), item.unit_name?.trim()]
        .filter(Boolean)
        .join(NBSP)
    );
  }
  if (typeof item.vpe === 'number' && item.vpe > 0) {
    parts.push(formatNumber(item.vpe) + NBSP + 'per' + NBSP + 'case');
  }
  if (
    typeof item.single_deposit_price === 'number' &&
    item.single_deposit_price > 0
  ) {
    parts.push(
      'deposit' + NBSP + formatPrice(item.single_deposit_price, currency)
    );
  }

  // The dot stays at the end of a line, never at the start of the next.
  return parts.join(NBSP + '· ');
}
